import { Core } from "@faire/design-tokens";
import {
  generateCSSBreakpointMapForImages,
  IImageByViewport,
} from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Banner/ByType/utils";
import { Context } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { optimizeImageUrlFillRectangle } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web--source/common/transform-images";
import { Column, Flex, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { Anchor } from "@faire/web--source/ui/LinkAnchor";
import { ILayoutElement } from "@faire/web-api--source/indigofair/data/ILayoutElement";
import React from "react";
import styled from "styled-components";

export const CommunityCategoryBanner: React.FC<{
  ref?: React.Ref<HTMLDivElement>;
}> = ({ ref }) => {
  const { layoutElement, onCTAClick, requestId } = React.useContext(Context);

  return (
    <CommunityCategoryBannerBase
      layoutElement={layoutElement}
      onCTAClick={onCTAClick}
      requestId={requestId}
      ref={ref}
    />
  );
};

interface IProps {
  layoutElement: ILayoutElement;
  onCTAClick?: (eventName: string, requestId: string) => void;
  requestId: string | undefined;
  ref?: React.Ref<HTMLDivElement>;
  className?: string;
}

export const CommunityCategoryBannerBase: React.FC<IProps> = ({
  layoutElement,
  onCTAClick,
  requestId,
  ref,
  className,
}) => {
  const isSSR = useIsSSR();
  const {
    main_text,
    images,
    title_icon,
    description_text,
    cta_text,
    cta_link,
    background_color,
    main_text_color,
    cta_text_color,
    on_click_event_name,
  } = layoutElement;

  const handleClick = () => {
    onCTAClick?.(
      on_click_event_name ?? "CommunityCategoryBanner",
      requestId ?? ""
    );
  };

  return (
    <Wrapper
      $backgroundColor={background_color}
      ref={ref}
      className={className}
    >
      <MessageContainer justify="center">
        <Typography variant="displaySSerifRegular" color={main_text_color}>
          {main_text}
        </Typography>
        <Spacer size={14} />
        <StyledRow>
          <Typography variant="paragraphSansRegular" color={main_text_color}>
            {description_text}
          </Typography>
        </StyledRow>
        <Spacer size={20} />
        <StyledRow>
          <StyledLink onClick={handleClick} target="_blank" href={cta_link}>
            <Typography variant="paragraphSansRegular" color={cta_text_color}>
              {cta_text}
            </Typography>
          </StyledLink>
        </StyledRow>
      </MessageContainer>
      <MessageImage
        $isSSR={isSSR}
        $images={generateCSSBreakpointMapForImages(images)}
      >
        {title_icon ? (
          <StyledFairTradeFederationIcon
            src={optimizeImageUrl(title_icon, isSSR, {
              resizeMethod: "fit",
              maintainTransparency: true,
            })}
          />
        ) : null}
      </MessageImage>
    </Wrapper>
  );
};

const Wrapper = styled(Flex).attrs({
  align: "center",
  justify: "space-between",
  direction: {
    mobileAndAbove: "column-reverse",
    tabletAndAbove: "row",
  },
})<{
  $backgroundColor: string | undefined;
}>`
  position: relative;
  width: 100%;
  height: 280px;
  background-color: ${({ $backgroundColor }) => $backgroundColor ?? null};

  ${responsiveMediaQueries.mobile} {
    height: auto;
  }
`;

const MessageContainer = styled(Column)`
  ${responsiveMediaQueries.mobile} {
    width: 100%;
    max-width: 100%;
    padding: ${getSpacing("2x", "2x", "3x")};
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    height: 100%;
    padding: ${getSpacing(0, "5x")};
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    padding: ${getSpacing(0, "6x")};
  }
  ${responsiveMediaQueries.xLargeAndAbove} {
    padding: ${getSpacing(0, "10x", 0, "6x")};
  }
`;

const StyledLink = styled(Anchor)`
  border-bottom: 1px solid ${Core.border.muted};
`;

const StyledRow = styled(Row).attrs({
  align: "start",
})``;

const MessageImage = styled(Column)<{
  $isSSR: boolean;
  $images: IImageByViewport;
}>`
  position: relative;
  height: 100%;
  width: 50%;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: left;

  ${responsiveMediaQueries.mobile} {
    min-height: 160px;
    width: 100%;
    background-image: url(${({ $isSSR, $images }) =>
      $images.mobile
        ? optimizeImageUrlFillRectangle($images.mobile, $isSSR, 375, 160)
        : null});
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    background-image: url(${({ $isSSR, $images }) =>
      $images.tablet
        ? optimizeImageUrlFillRectangle($images.tablet, $isSSR, 384, 280)
        : null});
    min-width: 384px;
  }
  ${responsiveMediaQueries.xLargeAndAbove} {
    background-image: url(${({ $isSSR, $images }) =>
      $images.xlarge
        ? optimizeImageUrlFillRectangle($images.xlarge, $isSSR, 550, 280, {
            resizeMethod: "fit",
          })
        : null});
    min-width: 550px;
  }
`;

const StyledFairTradeFederationIcon = styled.img`
  position: absolute;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: left;

  ${responsiveMediaQueries.mobile} {
    right: 20px;
    bottom: 20px;
    width: 68px;
    height: 70px;
  }
  ${responsiveMediaQueries.tabletAndAbove} {
    right: 32px;
    bottom: 32px;
    width: 82px;
    height: 84px;
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    right: 43px;
    bottom: 32px;
  }
  ${responsiveMediaQueries.xLargeAndAbove} {
    right: 40px;
    bottom: 40px;
    width: 105px;
    height: 108px;
  }
`;
